import { useCurrentOrgId } from '@/stores/authStore'
import request, { type ComplexResponseType } from './request'
import { useQuery } from '@tanstack/react-query'
import { zodNotificationResponseValidator, zodNotificationSettingResponseValidator, type NotificationResponse, type NotificationSettingResponse } from './types/Notification'
import { queryClient } from '@/utils/reactQuerty'

const getNotifications = async ({ page, limit }: { page: number, limit: number }) => {
  const res = await request<ComplexResponseType<NotificationResponse[]>>({
    endpoint: `/settings/notifications?page=${page}&limit=${limit}`,
    method: 'GET'
  })
  if (res.success) zodNotificationResponseValidator(res.data.items)
  return res
}

export const useGetNotifications = ({ page, limit }: { page: number, limit: number }) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'notifications', page, limit],
    queryFn: () => getNotifications({ page, limit }).then(data => {
      queryClient.invalidateQueries({ queryKey: [currOrgId, 'notifications', 'unread-count'] })
      return data
    }),
  })
}

export const useGetUnreadNotificationsCount = () => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'notifications', 'unread-count'],
    queryFn: () => request<number>({
      endpoint: '/settings/notifications/counts',
      method: 'GET',
    }),
  })
}

export const markNotificationAsRead = async (notificationId?: NotificationResponse['id']) => {
  return request({
    endpoint: '/settings/notifications/mark-as-read',
    method: 'POST',
    body: notificationId ? { notificationId } : undefined
  })
}

/* ---------------------------- Organization Notifications Settings ---------------------------- */

const getOrganizationNotificationsSettings = async () => {
  const res = await request<NotificationSettingResponse[]>({
    endpoint: '/organization/notifications-preferences',
    method: 'GET'
  })
  if (res.success) zodNotificationSettingResponseValidator(res.data)
  return res
}

export const useGetOrganizationNotificationsSettings = () => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization', 'notifications-preferences'],
    queryFn: getOrganizationNotificationsSettings
  })
}

export const updateOrganizationNotificationSettings = async (
  notificationSettingId: NotificationSettingResponse['id'],
  value: NotificationSettingResponse['value']
) => await request({
  endpoint: '/organization/notifications-preferences',
  method: 'PUT',
  body: { notificationSettingId, value }
})

/* ---------------------------- Organization Member Notifications Settings ---------------------------- */

const getOrgMemberNotificationsSettings = async (memberId: string) => {
  const res = await request<NotificationSettingResponse[]>({
    endpoint: `/organization/members/${memberId}/notifications-preferences`,
    method: 'GET'
  })
  if (res.success) zodNotificationSettingResponseValidator(res.data)
  return res
}

export const useGetOrgMemberNotificationsSettings = (memberId: string, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization-member', memberId, 'notifications-preferences'],
    queryFn: () => getOrgMemberNotificationsSettings(memberId),
    enabled
  })
}

export const updateOrgMemberNotificationSettings = async (
  memberId: string,
  notificationSettingId: NotificationSettingResponse['id'],
  value: NotificationSettingResponse['value']
) => await request({
  endpoint: `/organization/members/${memberId}/notifications-preferences`,
  method: 'PUT',
  body: { notificationSettingId, value }
})

/* ---------------------------- Organization Individual Notifications Settings ---------------------------- */

const getOrgIndividualNotificationsSettings = async (individualId: string) => {
  const res = await request<NotificationSettingResponse[]>({
    endpoint: `/organization/individuals/${individualId}/notifications-preferences`,
    method: 'GET'
  })
  if (res.success) zodNotificationSettingResponseValidator(res.data)
  return res
}

export const useGetOrgIndividualNotificationsSettings = (individualId: string, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'organization-individual', individualId, 'notifications-preferences'],
    queryFn: () => getOrgIndividualNotificationsSettings(individualId),
    enabled
  })
}

export const updateOrgIndividualNotificationSettings = async (
  individualId: string,
  notificationSettingId: NotificationSettingResponse['id'],
  value: NotificationSettingResponse['value']
) => await request({
  endpoint: `/organization/individuals/${individualId}/notifications-preferences`,
  method: 'PUT',
  body: { notificationSettingId, value }
})

/* -------------------------------------------------------------------------- */
